import { useState } from 'react';
import { Button, Input, Alert, AlertDescription, Spinner } from '@/components/ui';
import { Smartphone, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import {
  enrollTotpStart, enrollTotpVerify, enrollEmailStart, enrollEmailVerify, enrollPasskey,
  type MfaMethod, type EnrollResult,
} from '@/services/mfaService';

interface Props {
  orgId?: string;
  /** Methods already enrolled (hidden from the picker). */
  existing?: MfaMethod[];
  onEnrolled: (result: EnrollResult) => void;
  onCancel?: () => void;
  /**
   * The server demanded a satisfied second factor before this change (403
   * `mfa_required`). Hand control back so the host can run a step-up challenge
   * — showing the raw error here would be a dead end, since this component has
   * no way to satisfy it.
   */
  onStepUpRequired?: () => void;
}

const METHODS: { id: MfaMethod; label: string; desc: string; Icon: typeof Smartphone }[] = [
  { id: 'totp', label: 'Authenticator app', desc: 'Google Authenticator, 1Password, Authy…', Icon: Smartphone },
  { id: 'passkey', label: 'Passkey', desc: 'Face ID, Touch ID, or a security key', Icon: KeyRound },
  { id: 'email', label: 'Email code', desc: 'A one-time code sent to your email', Icon: Mail },
];

export default function MfaEnrollFlow({ orgId, existing = [], onEnrolled, onCancel, onStepUpRequired }: Props) {
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [totp, setTotp] = useState<{ qrDataUrl: string; manualKey: string } | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /** True when the failure was the step-up gate, which the host must resolve. */
  const handedOffAsStepUp = (e: any): boolean => {
    if (e?.code !== 'mfa_required' || !onStepUpRequired) return false;
    onStepUpRequired();
    return true;
  };

  const pick = async (m: MfaMethod) => {
    setError(''); setCode(''); setMethod(m); setLoading(true);
    try {
      if (m === 'totp') {
        const d = await enrollTotpStart(orgId);
        setTotp({ qrDataUrl: d.qrDataUrl, manualKey: d.manualKey });
      } else if (m === 'email') {
        const r = await enrollEmailStart(orgId);
        setEmailSentTo(r.sentTo);
      } else if (m === 'passkey') {
        const r = await enrollPasskey('Passkey', orgId);
        onEnrolled(r);
      }
    } catch (e: any) {
      setMethod(null);
      if (handedOffAsStepUp(e)) return;
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setError(''); setLoading(true);
    try {
      const r = method === 'totp'
        ? await enrollTotpVerify(code.trim(), 'Authenticator app', orgId)
        : await enrollEmailVerify(code.trim(), orgId);
      onEnrolled(r);
    } catch (e: any) {
      if (handedOffAsStepUp(e)) return;
      setError(e?.message || 'That code is incorrect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const back = () => { setMethod(null); setTotp(null); setEmailSentTo(null); setCode(''); setError(''); };

  if (!method) {
    return (
      <div className="space-y-3">
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {METHODS.filter((m) => !existing.includes(m.id)).map(({ id, label, desc, Icon }) => (
          <button
            key={id}
            onClick={() => pick(id)}
            disabled={loading}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border bg-card hover:bg-muted text-left transition-colors disabled:opacity-50"
          >
            <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
            <span className="flex-1">
              <span className="block font-medium text-foreground">{label}</span>
              <span className="block text-sm text-muted-foreground">{desc}</span>
            </span>
          </button>
        ))}
        {onCancel && <Button variant="ghost" className="w-full" onClick={onCancel}>Cancel</Button>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button onClick={back} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Choose a different method
      </button>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {method === 'totp' && totp && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app, then enter the 6-digit code it shows.</p>
          <img src={totp.qrDataUrl} alt="Authenticator QR code" className="w-44 h-44 mx-auto border border-border rounded-lg bg-white p-2" />
          <p className="text-xs text-center text-muted-foreground">Can't scan? Enter this key manually:<br /><code className="text-foreground break-all">{totp.manualKey}</code></p>
        </div>
      )}

      {method === 'email' && (
        <p className="text-sm text-muted-foreground">We sent a 6-digit code to <strong className="text-foreground">{emailSentTo}</strong>. Enter it below.</p>
      )}

      {(method === 'totp' || method === 'email') && (
        <div className="space-y-3">
          <Input
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={loading}
          />
          <Button className="w-full" onClick={verify} disabled={loading || code.length < 6}>
            {loading ? <Spinner size="sm" /> : 'Verify & enable'}
          </Button>
        </div>
      )}

      {method === 'passkey' && loading && (
        <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground"><Spinner size="sm" /> Waiting for your passkey…</div>
      )}
    </div>
  );
}
